/**
 * Batches section reads into one request.
 *
 * <SectionUpdate> components mount independently all over the tree. Naively that is
 * one request per section. Each instead registers its need here, and a single QUERY
 * flushes on the next microtask — which is the whole reason the endpoint takes an array.
 *
 * This is the DataLoader pattern. It knows nothing about React.
 */
import { FRAMEWORK_ROUTES, SectionStatus, type Lang, type SectionQueryResult, type SectionQueryResponse }
  from '@framework/core';
import { setCapabilities } from './capability-store';
import { flagEnabled } from '../flags/store';
import { FLAG_BATCH_LOG } from '../flags/built-in';

type Waiter = { resolve: (r: SectionQueryResult) => void; reject: (e: unknown) => void };

/** Set by the app so the loader can report the server's cache epoch back. */
let epochHandler: ((epoch: number | undefined) => void) | null = null;
export const setEpochHandler = (fn: typeof epochHandler): void => { epochHandler = fn; };

class SectionLoader {
  private queue = new Map<string, { version?: number; waiters: Waiter[] }>();
  private scheduled = false;
  private lang: Lang | undefined;

  load(name: string, lang: Lang, version?: number): Promise<SectionQueryResult> {
    this.lang = lang;
    return new Promise((resolve, reject) => {
      const entry = this.queue.get(name) ?? { version, waiters: [] };
      entry.waiters.push({ resolve, reject });
      this.queue.set(name, entry);

      if (!this.scheduled) {
        this.scheduled = true;
        queueMicrotask(() => void this.flush());
      }
    });
  }

  private async flush(): Promise<void> {
    const batch = this.queue;
    const lang = this.lang!;
    this.queue = new Map();
    this.scheduled = false;
    if (batch.size === 0) return;

    // The version we hold goes out with the request; the server answers CURRENT
    // (no payload) or MODIFIED (new content).
    const objects = [...batch.entries()].map(([name, e]) => ({ name, version: e.version }));

    try {
      const res = await fetch(FRAMEWORK_ROUTES.sectionQuery, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lang, objects }),
      });

      const payload = (await res.json()) as SectionQueryResponse & { epoch?: number };
      epochHandler?.(payload.epoch);
      setCapabilities(payload.results);

      if (flagEnabled(FLAG_BATCH_LOG)) {
        console.debug(
          `[cms] 1 request → ${objects.length} sections (${lang}):`,
          payload.results.map((r) => `${r.name}:${r.status}`).join(' '),
        );
      }

      const byName = new Map(payload.results.map((r) => [r.name, r]));
      for (const [name, e] of batch) {
        const r = byName.get(name) ?? { name, status: SectionStatus.NotFound };
        e.waiters.forEach((w) => w.resolve(r));
      }
    } catch (err) {
      for (const [, e] of batch) e.waiters.forEach((w) => w.reject(err));
    }
  }
}

export const sectionLoader = new SectionLoader();
